<template>
    <li
        class="notification-item"
        :class="dynamicClasses"
        @mouseover="notificationItemHovered = true"
        @mouseleave="notificationItemHovered = false"
    >
        <div
            class="black notification-message-container cpointer pr-3 py-2 pl-2"
            @click="onClickBanner"
        >
            <template v-if="isEmailNotification">
                <NotificationEmail type="item" :notification="item.notif" />
            </template>
            <NotificationReminderActivity
                v-else-if="isReminderActivity"
                :notification="item.notif"
                :context="NOTIFICATION_CONTEXT.NOTIFICATION_CENTER"
            />
            <div v-else class="notification-content-container">
                <CompassLogo v-if="showLogo" type="icon" theme="light" :width="32" />
                <CompassAvatar
                    v-else
                    size="small"
                    :initials="getInitialsAvatar"
                    :src="getSourceAvatar"
                />
                <p v-if="item.isSystem">
                    <template v-if="isConsentNotification">
                        <CompassTooltip contentClass="notification-item-tooltip">
                            <template #content>
                                <span class="semibold">{{ item.linkedObject.name }} </span>
                            </template>
                            <span class="notification-item-name">
                                {{ item.linkedObject.name }} :
                            </span>
                        </CompassTooltip>
                        <CompassTooltip
                            v-if="targetedStaffFullName"
                            contentClass="notification-item-tooltip"
                        >
                            <template #content>
                                <span class="semibold">{{ targetedStaffFullName }}</span>
                            </template>
                            <span class="notification-item-name">
                                {{ targetedStaffFullName }}
                            </span>
                        </CompassTooltip>
                        <span>&nbsp;{{ item.message }}</span>
                    </template>
                    <template v-else>
                        <CompassTooltip contentClass="notification-item-tooltip">
                            <template #content>
                                <span class="semibold">{{ item.linkedObject.name }} </span>
                            </template>
                            <span class="notification-item-name">
                                {{ item.linkedObject.name }}
                            </span>
                        </CompassTooltip>
                        <span>: {{ item.message }}</span>
                    </template>
                </p>
                <p v-else>
                    <span class="semibold">
                        {{ $filters.capitalize(item.sender.firstname) }}
                        {{ $filters.capitalize(item.sender.lastname) }}
                    </span>
                    <span>&nbsp;{{ item.message }} {{ getTargetLabel() }}</span>
                    <span class="semibold">&nbsp;{{ item.linkedObject.name }}</span>
                </p>
            </div>
            <div class="notification-actions-container">
                <div class="flex-right">
                    <CompassMenu
                        placement="bottom-end"
                        customClass="notification-actions-container__menu"
                        @click.stop
                        @open="displayActions = true"
                        @close="displayActions = false"
                    >
                        <CompassIconButton :class="menuTriggerClasses" icon="action" />
                        <template #dropdown>
                            <CompassMenuItem
                                prefixIcon="bin"
                                :label="$l10n.__('Supprimer cette notification')"
                                @click="deleteNotification"
                            />
                            <CompassMenuItem
                                v-if="item.read !== null"
                                prefixIcon="check"
                                :label="$l10n.__('Marquer comme non lu')"
                                @click="markAsUnRead"
                            />
                        </template>
                    </CompassMenu>
                </div>
                <div class="flex-right notification-chip-container">
                    <NotificationChip v-show="item.read === null" @clicked="markAsRead" />
                </div>
            </div>
        </div>
    </li>
</template>

<script>
import NotificationChip from 'vcomponents/utilities/NotificationChip.vue';
import { getFullNameUser } from 'Helpers/avatarHelper';
import { getTarget } from 'js/config/authenticated/dictionary/target.js';
import Notification from 'Services/Notification/Notification';
import NotificationsMixins from 'mixins/NotificationsMixins.js';
import NotificationEmail from 'vcomponents/notifications/emails/NotificationEmail.vue';
import NotificationReminderActivity from 'vcomponents/notifications/activity/NotificationReminderActivity.vue';
import { NOTIFICATION_CONTEXT, EVENT_TYPES } from 'Dictionaries/notification/NotificationEnums';

export default {
    components: {
        NotificationReminderActivity,
        NotificationChip,
        NotificationEmail,
    },
    mixins: [NotificationsMixins],
    props: {
        item: {
            type: Notification,
            required: true,
            default: () => {},
        },
    },
    data() {
        return {
            notification: null,
            notificationItemHovered: false,
            notificationMenuHovered: false,
            isPopoverHovered: false,
            displayMenuActions: false,
            displayActions: false,
            NOTIFICATION_CONTEXT,
        };
    },
    computed: {
        isConsentNotification() {
            return (
                this.item.event === EVENT_TYPES.THIRD_AUTHORIZATION_ACCEPTED ||
                this.item.event === EVENT_TYPES.THIRD_AUTHORIZATION_REFUSED
            );
        },
        isEmailNotification() {
            return this.item.event === EVENT_TYPES.EMAIL_RECEIVED_NOTIFICATION;
        },
        isReminderActivity() {
            return this.item.event === EVENT_TYPES.AGENDA_ALERT_SENT;
        },
        targetedStaffFullName() {
            return getFullNameUser(this.item.targetedStaff);
        },
        dynamicClasses() {
            let style;
            if (this.notificationItemHovered && !this.notificationMenuHovered) {
                style = 'hover-primary-05-bkg';
            } else if (this.notificationMenuHovered) {
                style = 'hover-white-bkg';
            }
            return style;
        },
        showLogo() {
            return this.item.isSystem || this.item.sender.avatar.type === 'sellsyuserpic';
        },
        getInitialsAvatar() {
            return this.item.sender.avatar.type === 'initials' ? this.item.sender.avatar.value : '';
        },
        getSourceAvatar() {
            return this.item.sender.avatar.type === 'picture' ? this.item.sender.avatar.value : '';
        },
        menuTriggerClasses() {
            return [
                this.notificationItemHovered || this.displayActions
                    ? 'notification-item-actions-menu'
                    : 'notification-item-actions-menu--hidden',
            ];
        },
    },
    methods: {
        markAsRead() {
            EventBus.emit('notification:mark-as-read', this.item.notifId);
        },
        markAsUnRead() {
            EventBus.emit('notification:mark-as-unread', this.item.notifId);
        },
        deleteNotification() {
            this.$emit('delete', this.item.notifId);
        },
        closePanelNotif() {
            EventBus.emit('notification:panel:close');
        },
        onClickBanner() {
            this.markAsRead();
            if (this.item.onClick) {
                this.closePanelNotif();
                this.item.onClick();
            } else {
                Utils.relocate(this.item.path, true);
            }
        },
        getTargetLabel() {
            return this.item.linkedObject?.type ? getTarget(this.item.linkedObject.type) : '';
        },
    },
};
</script>

<style lang="scss">
.notification-item {
    &-tooltip {
        z-index: 20001 !important;
    }
    &-name {
        max-width: 200px;
        display: inline-block;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        font-weight: var(--font-weights-lato-1);
        vertical-align: middle;
    }
}
</style>

<style scoped lang="scss">
@use '~@sellsy/styleguide/src/_variables' as *;

.notification-item {
    border-radius: 4px;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;

    &-actions-menu {
        visibility: visible;

        &--hidden {
            visibility: hidden;
        }
    }

    .notification-item-content {
        width: 370px;
    }
    .notification-item-time {
        margin: 2px 0 0 40px;
    }
    .icon-container {
        width: 32px;
        height: 32px;
        cursor: pointer;
        border-radius: 4px;
        display: true;
        margin-left: 16px;
    }
    &:hover {
        .icon-container {
            display: flex;
        }
    }
    .notification-content-container {
        display: flex;
        align-items: center;
        gap: var(--spacing-1);
    }
    .notification-actions {
        visibility: hidden;
        &.display-actions {
            visibility: visible;
        }
        &-container {
            display: flex;
            gap: var(--spacing-1);
            height: var(--spacing-5);
        }
    }
    .notification-chip-container {
        align-self: flex-start;
        width: 24px;
    }
    .notification-message-container {
        display: flex;
        flex-direction: row;
        flex-grow: 1;
        align-items: center;
        justify-content: space-between;
        gap: var(--spacing-1);
        padding: var(--spacing-0) 0;
        .compass-logo {
            background-color: $secondary-blue;
            flex-shrink: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 50%;
            height: 32px;
            :deep(svg) {
                height: 20px;
                width: 20px;
            }
        }
    }
    p {
        margin: 0;
        line-height: 20px;
        font-size: 14px;
        text-align: left;
        word-break: break-word;
    }
}
</style>

<style lang="scss">
#compass-menu-container {
    .notification-actions-container__menu {
        z-index: 2002;
    }
}
</style>
